import { useTranslation } from "react-i18next";
import { useRunsPublicProcurements } from "../../organization/useRunsPublicProcurements";

/**
 * Resolves a sidebar nav key to its translated label.
 *
 * The nav is data-driven (keys come from navConfig), so `t(key)` would be invisible to
 * i18next-parser and the keys would get pruned from the locale files. Listing them here as
 * literal `t("…")` calls (the useXxxLabel pattern) keeps them extracted and translatable.
 *
 * Only the wording switches — the keys stay put, so the `?status=` URLs in bookmarks still resolve.
 */
export const useNavLabel = () => {
  const { t } = useTranslation();
  const runsPublicProcurements = useRunsPublicProcurements();
  const privateOrganisationLabels: Record<string, string> = {
    Announced: t("Invitations sent"),
    "Award and standstill": t("Award"),
    Procurements: t("Sourcing"),
    "All procurements": t("All sourcing events"),
    "New procurement": t("New sourcing event"),
  };
  const labels: Record<string, string> = {
    Overview: t("Overview"),
    Assistant: t("Assistant"),
    Companies: t("Companies"),
    Needs: t("Needs"),
    Market: t("Market"),
    Procurements: t("Procurements"),
    Contracts: t("Contracts"),
    Projects: t("Projects"),
    "New need": t("New need"),
    Inbox: t("Inbox"),
    "My needs": t("My needs"),
    "Needs overview": t("Needs overview"),
    "Approved needs": t("Approved needs"),
    "All procurements": t("All procurements"),
    "In progress": t("In progress"),
    Announced: t("Announced"),
    "Under evaluation": t("Under evaluation"),
    "Award and standstill": t("Award and standstill"),
    Completed: t("Completed"),
    "All contracts": t("All contracts"),
    "Active contracts": t("Active contracts"),
    "My contracts": t("My contracts"),
    "Search in contracts": t("Search in contracts"),
    "Contract tasks": t("Contract tasks"),
    "Find procurements": t("Find procurements"),
    "My bids": t("My bids"),
    "My invitations": t("My invitations"),
    "My contributions": t("My contributions"),
    "All projects": t("All projects"),
    "New procurement": t("New procurement"),
    "New contract": t("New contract"),
    "New project": t("New project"),
    Templates: t("Templates"),
    "Requirement library": t("Requirement library"),
    Library: t("Library"),
  };
  return (key: string): string =>
    (runsPublicProcurements ? undefined : privateOrganisationLabels[key]) ?? labels[key] ?? key;
};
